import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type Props = {
  /** Effet destructeur confirmé : réimport d'un Live ou remise à zéro. */
  effect: 'IMPORT_LIVE' | 'RESET';
  /** Titre du Live dont les interactions seront reprises. */
  sourceTitle: string | null;
  /** Date de la prochaine session programmée, déjà formatée. */
  nextSessionLabel: string | null;
  onCancel: () => void;
  onConfirm: () => void;
};

export default function EvergreenSourcesChangeDialog({
  effect,
  sourceTitle,
  nextSessionLabel,
  onCancel,
  onConfirm,
}: Props) {
  const { t } = useTranslation('createWebinar');
  const importsLive = effect === 'IMPORT_LIVE';

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {t(
              importsLive
                ? 'sessionForm.sources.confirm.replaceTitle'
                : 'sessionForm.sources.confirm.resetTitle',
            )}
          </DialogTitle>
          <DialogDescription>
            {importsLive
              ? t('sessionForm.sources.confirm.replaceDescription', { title: sourceTitle ?? '' })
              : t('sessionForm.sources.confirm.resetDescription')}
          </DialogDescription>
        </DialogHeader>

        <p className="flex items-center gap-2 text-sm font-medium text-error">
          <AlertTriangle size={16} className="shrink-0" aria-hidden />
          <span>{t('sessionForm.sources.confirm.irreversible')}</span>
        </p>

        {importsLive && nextSessionLabel && (
          <p className="text-sm text-secondary">
            {t('sessionForm.sources.confirm.nextSessionWarning', { date: nextSessionLabel })}
          </p>
        )}

        <DialogFooter className="w-full flex justify-between gap-4">
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
            {t('sessionForm.mp4.dialog.cancel')}
          </Button>
          <Button type="button" className="flex-1" onClick={onConfirm}>
            {t(
              importsLive
                ? 'sessionForm.sources.confirm.confirmReplace'
                : 'sessionForm.sources.confirm.confirmReset',
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
